<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router'
import { ref, watch } from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/stores/auth-store'
import { ResourceResponse, uuid } from '@/types/general'
import { TransitCategoryType, TransitReport } from '@/types/damage-report'
import { image as imageHelper } from '@/utils/assets'
import { datetime } from '@/utils/dates'
import { transitCategoryTypes } from '@/utils/damage-report'

import MyModal from '@/components/my-components/MyModal.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MapComponent from '@/components/MapComponent.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import MyImageViewer from '@/components/my-components/MyImageViewer.vue'

export interface Props {
    open?: boolean
}

const props = withDefaults(defineProps<Props>(), { open: true })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const loading = ref(false)
const transitReport = ref<TransitReport>()

function formatDate(date: string | undefined) {
    if (!date) return t('notSpecified')
    return datetime(date)
}

async function fetchTransitReport() {
    const transitReportId = route.params.transitReportId as uuid | undefined
    if (!transitReportId) return
    loading.value = true

    try {
        const url = window.route('dmr.company.transit-reports.show', {
            company: authStore.companyId,
            transit_report: transitReportId,
        })

        const response = await axios.get<ResourceResponse<TransitReport>>(url)
        transitReport.value = response.data.data
    } finally {
        loading.value = false
    }
}

async function onClose() {
    router.replace({ name: 'dmr.transit-reports', query: route.query })
}

watch(() => route.params.transitReportId, fetchTransitReport, { immediate: true })
</script>

<template>
    <MyModal :value="props.open" :max-width="1600" :no-padding="!loading" @close="onClose()">
        <LoaderWrapper :visible="loading" />
        <div v-if="transitReport" class="flex flex-row">
            <!-- First Section -->
            <div class="flex w-full flex-col space-y-6 p-6 :flex-col">
                <div>
                    <h3
                        class="text-lg font-semibold uppercase text-primary-400"
                        v-text="t('transitReport')"
                    />
                </div>

                <div class="flex justify-between space-x-3">
                    <div class="flex w-1/2 flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('company')"
                        />
                        <span
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="transitReport?.company.name ?? t('notSpecified')"
                        />

                        <img
                            v-if="transitReport?.company?.logo"
                            class="mt-2 w-24 rounded-xl bg-primary-200"
                            :src="imageHelper(transitReport?.company.logo, 'thumbnail')"
                        />
                    </div>
                    <div class="flex w-1/2 flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('haulier')"
                        />
                        <span
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="transitReport?.haulierCompany?.name ?? t('notSpecified')"
                        />

                        <img
                            v-if="transitReport?.haulierCompany?.logo"
                            class="mt-2 w-20 rounded"
                            :src="imageHelper(transitReport?.haulierCompany.logo, 'thumbnail')"
                        />
                    </div>
                </div>

                <div class="space-y-6">
                    <div class="mx-auto flex w-full break-inside-avoid flex-col justify-center">
                        <div
                            class="flex items-center justify-between space-y-1 rounded-t-xl bg-primary-50 px-4 py-3 dark:bg-dark-500 print:px-0"
                        >
                            <div class="flex flex-col">
                                <span
                                    class="text-xs text-primary-400 dark:text-primary-100"
                                    v-text="formatDate(transitReport?.transpiredAt)"
                                />
                            </div>

                            <div class="flex items-center space-x-3">
                                <span
                                    class="text-sm font-semibold text-primary-500 dark:text-primary-300"
                                    v-text="transitReport.user.name"
                                />

                                <img
                                    v-if="transitReport.user.profileImage"
                                    :alt="transitReport.user.name"
                                    class="mr-2.5 w-10 rounded-full object-cover"
                                    :src="imageHelper(transitReport.user.profileImage)"
                                />
                            </div>
                        </div>

                        <div class="relative h-[200px] rounded-b-xl shadow-xl">
                            <MapComponent
                                class="h-full"
                                :markers="[{ location: transitReport.location }]"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <!-- Second Section -->
            <div class="w-full p-6">
                <div>
                    <h3
                        class="text-lg font-semibold uppercase text-primary-400"
                        v-text="t('damageCategories')"
                    />
                    <div class="flex flex-col gap-6 mt-6">
                        <MyPanel
                            v-for="category in transitReport.categories"
                            :key="category.id"
                            class="p-4 flex justify-between"
                        >
                            <div class="flex flex-col gap-2 w-1/2 mr-4">
                                <div
                                    class="flex items-center gap-2 text-primary-500 dark:text-primary-300"
                                >
                                    <mdi:truck
                                        v-if="
                                            category.type ===
                                            TransitCategoryType.DamageOwnTruckTrailer
                                        "
                                        class="w-5 h-5"
                                    />
                                    <mdi:package-variant
                                        v-if="category.type === TransitCategoryType.DamageGoods"
                                        class="w-5 h-5"
                                    />
                                    <mdi:toolbox
                                        v-if="
                                            category.type ===
                                            TransitCategoryType.DamageOtherMaterials
                                        "
                                        class="w-5 h-5"
                                    />
                                    <mdi:alert
                                        v-if="
                                            category.type === TransitCategoryType.DamageCounterPart
                                        "
                                        class="w-5 h-5"
                                    />
                                    <span
                                        class="text-md font-semibold text-primary-500 dark:text-primary-300"
                                        v-text="t(transitCategoryTypes[category.type])"
                                    />
                                </div>

                                <div v-text="category.note" />
                            </div>
                            <MyImageViewer as-row>
                                <img
                                    v-for="image in category.images"
                                    :key="image"
                                    class="ml-2 mb-2 w-20 self-center rounded hover:cursor-pointer"
                                    :src="imageHelper(image!, 'thumbnail')"
                                    :data-src="imageHelper(image!, 'public')"
                                />
                            </MyImageViewer>
                        </MyPanel>
                    </div>
                </div>
            </div>
        </div>
    </MyModal>
</template>
